import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, ArrowLeft, Smartphone, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { forgotPasswordSms, resetPasswordSms } = useAuth();
  const [method, setMethod] = useState("email"); // email | sms
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  // SMS flow
  const [smsStep, setSmsStep] = useState("request"); // request | reset
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch { setSent(true); } // réponse générique
    finally { setLoading(false); }
  };

  const requestSms = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await forgotPasswordSms(email);
      setSmsStep("reset");
      toast.success(r.sms_sent_to ? `Code envoyé par SMS au ${r.sms_sent_to}` : "Si un compte existe, un code SMS a été envoyé.");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const submitSmsReset = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (pwd !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await resetPasswordSms(email, code.trim(), pwd);
      toast.success("Mot de passe réinitialisé. Connectez-vous.");
      navigate("/login");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafc] px-4">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-sm animate-fade-up" data-testid="forgot-password-page">
        <p className="overline text-[#C6A87C]">RADAR</p>

        {sent ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <MailCheck className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl text-[#0F172A]">{t("reset.sentTitle")}</h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="forgot-sent-desc">{t("reset.sentDesc")}</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] underline">
              <ArrowLeft className="h-4 w-4" /> {t("reset.backToLogin")}
            </Link>
          </div>
        ) : (
          <div className="mt-4">
            <h1 className="font-display text-3xl tracking-tight text-[#0F172A]">{t("reset.title")}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("reset.subtitle")}</p>

            {/* Choix de la méthode */}
            <div className="mt-5 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1" data-testid="forgot-method-toggle">
              <button type="button" data-testid="forgot-method-email" onClick={() => { setMethod("email"); setError(""); }}
                className={`flex items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition ${method === "email" ? "bg-white text-[#0F172A] shadow" : "text-slate-500"}`}>
                <Mail className="h-4 w-4" /> Par e-mail
              </button>
              <button type="button" data-testid="forgot-method-sms" onClick={() => { setMethod("sms"); setError(""); }}
                className={`flex items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition ${method === "sms" ? "bg-white text-[#0F172A] shadow" : "text-slate-500"}`}>
                <Smartphone className="h-4 w-4" /> Par SMS
              </button>
            </div>

            {method === "email" ? (
              <form onSubmit={submitEmail} className="mt-5" data-testid="forgot-password-form">
                <Label htmlFor="email" className="text-slate-700">{t("login.email")}</Label>
                <Input id="email" type="email" required value={email} data-testid="forgot-email"
                  onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="you@example.com" />
                <Button type="submit" disabled={loading} data-testid="forgot-submit"
                  className="mt-6 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("reset.sendBtn")}
                </Button>
              </form>
            ) : smsStep === "request" ? (
              <form onSubmit={requestSms} className="mt-5" data-testid="forgot-sms-request-form">
                <Label htmlFor="email-sms" className="text-slate-700">{t("login.email")}</Label>
                <Input id="email-sms" type="email" required value={email} data-testid="forgot-sms-email"
                  onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="you@example.com" />
                <p className="mt-2 text-xs text-slate-500">Un code à 6 chiffres sera envoyé au numéro de téléphone associé à votre compte.</p>
                {error && <p className="mt-3 text-sm text-red-600" data-testid="forgot-sms-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="forgot-sms-request-submit"
                  className="mt-6 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recevoir le code par SMS"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submitSmsReset} className="mt-5 space-y-4" data-testid="forgot-sms-reset-form">
                <div>
                  <Label className="text-slate-700">Code SMS (6 chiffres)</Label>
                  <Input value={code} maxLength={6} data-testid="forgot-sms-code"
                    onChange={(e) => setCode(e.target.value)} className="mt-1.5 h-11" placeholder="123456" />
                </div>
                <div>
                  <Label className="text-slate-700">Nouveau mot de passe</Label>
                  <Input type="password" value={pwd} data-testid="forgot-sms-pwd"
                    onChange={(e) => setPwd(e.target.value)} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-slate-700">Confirmer le mot de passe</Label>
                  <Input type="password" value={confirm} data-testid="forgot-sms-confirm"
                    onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="forgot-sms-reset-error">{error}</p>}
                <Button type="submit" disabled={loading || code.trim().length < 6} data-testid="forgot-sms-reset-submit"
                  className="h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réinitialiser mon mot de passe"}
                </Button>
              </form>
            )}

            <Link to="/login" className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 hover:text-[#0F172A]">
              <ArrowLeft className="h-4 w-4" /> {t("reset.backToLogin")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
